import {
  AfterViewChecked,
  Component,
  ComponentRef, ElementRef, OnDestroy,
  OnInit,
  QueryList, Renderer2, ViewChild,
  ViewChildren,
  ViewContainerRef
} from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { SettingsService } from '../../services/settings.service';
import { SettingsEventType } from '../../models/enums/settingsEventType';
import { NotifierService } from 'angular-notifier';
import { UserstoryService } from '../../services/userstory.service';
import { Userstory } from '../../models/userstory';
import { CdkDragDrop, CdkDragEnter, CdkDragExit, CdkDragMove, CdkDragStart } from '@angular/cdk/drag-drop';
import { Issue } from '../../models/issue';
import { Status } from '../../models/status';
import { Task } from '../../models/task';
import { IssueComponent } from '../../components/issue/issue.component';
import { Statuscategory } from '../../models/statuscategory';
import { SprintPlanning2Service } from '../../services/sprint-planning2.service';
import { AwallComponentEvent } from '../../models/awallComponentEvent';
import { Sprintplanning2SynchroHelper } from './sprintplanning2-synchro-helper';
import { SynchronizationService } from '../../services/synchronization.service';
import { HTMLElementPositioningHelper } from '../../util/htmlelement-positioning-helper';
import { ProductBacklogComponent } from '../../components/product-backlog/product-backlog.component';
import { BoardData } from '../../models/boardData';
import { BaseBoardComponent } from '../baseBoard';
import { CommunicationService } from '../../services/communication.service';
import { ProjectService } from '../../services/project.service';

@Component({
  selector: 'app-sprintplanning2-view',
  templateUrl: './sprintplanning2-view.component.html',
  styleUrls: ['./sprintplanning2-view.component.scss'],
  })
export class Sprintplanning2ViewComponent extends BaseBoardComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChildren('newIssueComponent', { read: ViewContainerRef }) newIssueComponentChildren: QueryList<ViewContainerRef>;
  @ViewChildren(IssueComponent) issueComponentChildren: QueryList<IssueComponent>;
  @ViewChildren(IssueComponent, { read: ElementRef }) issueComponentChildrenElementRef: QueryList<ElementRef>;
  @ViewChild(ProductBacklogComponent) productBacklog: ProductBacklogComponent;
  public issues: Userstory[] = [];
  public columnStatuses: Status[] = [];
  public disableScrollLeftBtn = true;
  public disableScrollRightBtn = false;
  public projectKey: string;
  private lastSyncScrollPercentageValue;
  private sprintId: number;
  public static readonly componentId = 'Sprintplanning2ViewComponent';

  constructor(
    private userStoryService: UserstoryService,
    private projectService: ProjectService,
    private settingsService: SettingsService,
    private communicationService: CommunicationService,
    private notifier: NotifierService,
    private sprintPlanning2Service: SprintPlanning2Service,
    private renderer: Renderer2,
    private syncHelper: Sprintplanning2SynchroHelper,
    private sync: SynchronizationService,
    private positionHelper: HTMLElementPositioningHelper
  ) {
    super();
    this.projectKey = settingsService.getProjectName();
    this.sprintId = settingsService.getSprintId(this.projectKey);
  }

  ngOnInit(): void {
    this.syncHelper.initSyncEventListeners(this);
    this.communicationService.registerComponent(Sprintplanning2ViewComponent.componentId, this);
    this.subscriptions.push(this.settingsService.getUpdateObservable().subscribe(settingsEventType => {
      if (settingsEventType === SettingsEventType.PROJECT_CHANGED) {
        this.projectKey = this.settingsService.getProjectName();
      }
    }));
    this.subscriptions.push(this.settingsService.getUpdateObservable().subscribe(settingsEventType => {
      if (settingsEventType === SettingsEventType.SPRINT_CHANGED) {
        this.projectKey = this.settingsService.getProjectName();
        this.sprintId = this.settingsService.getSprintId(this.projectKey);
        this.updateViewData();
      }
    }));
    // Store the latest received scroll sync-event to prevent sending the event back
    this.sync.subscribeEvent<any>('visibleColumnsChange').subscribe(data => {
      this.lastSyncScrollPercentageValue = data.scrollPercentage;
    });
  }

  ngOnDestroy(): void {
    this.communicationService.unregisterComponent(Sprintplanning2ViewComponent.componentId);
    for (const ev of this.subscriptions) {
      if (ev) {
        ev.unsubscribe();
      }
    }
  }

  ngAfterViewChecked() {
    // We are going to update the DOM after check
    // To prevent a ExpressionChangedAfterItHasBeenCheckedError do this async
    setTimeout(() => {
      // Get the current client and scrollWidth of the issue columns
      // If all columns are already visible, disable the right scroll button
      const scrollContainer: HTMLElement = document.querySelector('#sp2-column-view');
      if (scrollContainer) {
        this.disableScrollRightBtn = scrollContainer.clientWidth >= scrollContainer.scrollWidth;
      }
    });
  }

  /**
   * this requests the userstory and boardstate data and starts to sort it when both are downloaded
   */
  updateViewData() {
    // We clear the current data
    this.clearViewData();

    // We can only load new data if we have a project and a sprint to load
    if (!this.projectKey || !this.sprintId) {
      return;
    }

    this.notifier.notify('info', 'Loading Data...');
    forkJoin([
      this.userStoryService.getIssuesFromSprint(this.projectKey, this.sprintId, true, false, 'story, task, bug', true),
      this.projectService.getBoardStates(this.projectKey),
    ]).subscribe(([userstories, boardConfiguration]) => {
      this.columnStatuses = boardConfiguration.getAllStatuses();
      // We clear the current data again here. There might have been another async call filling
      // in data while this async has been waiting and we would get duplicat data.
      this.clearViewData();
      this.issues = userstories;
    });
  }

  /**
   * Clear all data held by this view. This will result in an empty board.
   */
  clearViewData() {
    this.issues = [];
  }

  // TODO check if we can do this the same way as in taskboard-view with issuecardhelper
  /**
   * Open a dialog (IssueCard) for the creation of a new Subtask
   */
  openNewSubissueDialog(event: any, issueId: number) {
    const newTaskId: number = this.userStoryService.getTemporaryIdForNewIssue();
    const issueComp = this.createIssueComponent(issueId, newTaskId);

    // Show the IssueCard modal window
    issueComp.createIssuecardForIssue(event, issueComp.issue, true);
  }

  // TODO try to remove and replace by code already in issuecardhelper
  /**
   * Create a new issueComponent for task creation inside story
   * @param issueId parent story ID
   * @param taskId ID of task to be created
   */
  createIssueComponent(issueId, taskId) {
    // Search for the correct row/column in which the new issue should be placed
    let issueIndex = null;

    this.issues.forEach((i, index) => {
      if (Number(i.id) === Number(issueId)) {
        issueIndex = index;
      }
    });
    const issueComponent = this.newIssueComponentChildren.toArray()[issueIndex];
    // Create new Task
    const statusTodo: Status = new Status('', '', this.columnStatuses[0].id, 'To Do', new Statuscategory('blue-gray', 2, 'new'));
    const newIssue: Task = new Task(taskId, '', '', 0, 0, null, null, statusTodo, issueId, [], [], '', false, [], '', '', '');

    // Use dynamically create a new Issue in the row
    const componentRef: ComponentRef<IssueComponent> = issueComponent.createComponent(IssueComponent);
    const issue: IssueComponent = componentRef.instance;
    issue.issue = newIssue;
    issue.projectKey = this.projectKey;
    issue.componentRef = componentRef;

    componentRef.instance.subIssueCreated.subscribe(createdSubIssue => {
      this.onNewSubIssueCreated(createdSubIssue);
    });

    return issue;
  }

  onCommunicationEvent(event: AwallComponentEvent): void {
    super.onCommunicationEvent(event);
  }

  getConnectedIssues(): any[] {
    return this.sprintPlanning2Service.getConnectedIssues();
  }

  getConnectedSubissues(): any[] {
    /* TODO: called multiple times because of interactjs from informationview */
    return this.issues.map(us => `story-task-container-id-${us.id}`);
  }

  onNewIssueCreated(issue: Issue): void {
    if (issue instanceof Userstory) {
      this.issues.push(issue);
    }
  }

  onNewSubIssueCreated(issueToCreate: Issue): void {
    this.issues.forEach(issue => {
      if (Number(issue.id) === Number(issueToCreate.parentIssueId)) {
        if (issueToCreate instanceof Task) {
          issue.tasks.push(issueToCreate);
        }
      }
    });
  }

  onIssueRemoved(id: number): void {
    this.issues.forEach((issue, index) => {
      if (Number(issue.id) === Number(id)) {
        this.issues.splice(index, 1);
      }
    });
  }

  onSubIssueRemoved(id: number): void {
    this.issues.forEach(issue => {
      issue.tasks.forEach((subissue, index) => {
        if (Number(subissue.id) === Number(id)) {
          issue.tasks.splice(index, 1);
        }
      });
    });
  }

  onIssueMoveStarted(event: CdkDragStart<Userstory>) {
    this.sync.sendRealTimeEvent('IssueMoveStarted', { 'id': event.source.data.id, 'source': 'sp2' });
  }

  onSubIssueMoveStarted(event: CdkDragStart<Userstory>) {
    this.onIssueMoveStarted(event);
  }

  onIssueMoved(event: CdkDragMove) {
    const position = this.positionHelper.getRelativePosition(event.pointerPosition.x, event.pointerPosition.y);
    const data = {
      'id': event.source.data.id,
      'position': position,
      'source': 'sp2'
    };
    this.sync.sendRealTimeEvent('IssueMoved', data);
  }

  onSubIssueMoved(event: CdkDragMove<Task>) {
    this.onIssueMoved(event);
  }

  onIssueDrop(event: CdkDragDrop<Userstory[]>) {
    this.sprintPlanning2Service.onIssueDrop(event);
  }

  onIssueDragListEnter(event: CdkDragEnter<any>) {
    // @ts-ignore
    this.renderer.setStyle(...this.sprintPlanning2Service.onIssueDragListEnter(event));
  }

  onIssueDragListEnterExit(event: CdkDragExit<any>) {
    // @ts-ignore
    this.renderer.setStyle(...this.sprintPlanning2Service.onIssueDragListEnterExit(event));
  }

  onSubissueDrop(event: CdkDragDrop<Userstory>) {
    this.sprintPlanning2Service.onSubissueDrop(event);
  }

  getIssueComponent(issueId: number): IssueComponent {
    return this.issueComponentChildren.find(child => Number(child.issue.id) === Number(issueId));
  }

  getNativeIssueComponent(issueId: number): ElementRef {
    return this.issueComponentChildrenElementRef.find(child => Number(child.nativeElement.dataset.issueid) === Number(issueId));
  }

  splitIssue(issueToClone: Userstory) {
    // By default, the Summary field of a cloned issue is prefixed with the string 'CLONE - ' to indicate that the issue is a clone.
    const cloneIssueSummary = `CLONE - ${issueToClone.summary}`;

    this.userStoryService.createIssue(
      this.projectKey,
      issueToClone.issueType,
      cloneIssueSummary,
      issueToClone.description,
      issueToClone.assignee,
      issueToClone.reporter,
      issueToClone.status.id,
      this.sprintId
    ).subscribe(createdIssueData => {
      if (createdIssueData) {
        const createdIssue: Issue = this.userStoryService.mapJSONToUserstory(createdIssueData, this.projectKey);
        const index: number = this.issues.lastIndexOf(issueToClone) + 1;
        this.sprintPlanning2Service.addIssueAtIndex(this.issues, createdIssue, index);
        this.sync.sendPersistenceEvent('addSplitIssueAtIndex',
          {
            'issue': createdIssue,
            'index': index
          });
      } else {
        console.log('Issue could NOT be created.');
      }
    });
  }

  /**
   * When scrolling reached one end, disable the scroll control buttons
   * Send sync-event containing the current scroll percentage
   * @param event
   */
  onColumnViewScroll(event: Event) {
    const scrollContainer: HTMLElement = document.querySelector('#sp2-column-view');

    this.disableScrollLeftBtn = scrollContainer.scrollLeft === 0;
    this.disableScrollRightBtn = scrollContainer.offsetWidth + Math.floor(scrollContainer.scrollLeft) >= scrollContainer.scrollWidth;

    const scrollPercentage = 100 * scrollContainer.scrollLeft / (scrollContainer.scrollWidth - scrollContainer.clientWidth);

    // Compare the current scrolling percentage with the latest received sync-event to prevent sending the event back
    if (Math.floor(this.lastSyncScrollPercentageValue) !== Math.floor(scrollPercentage)) {
      this.sync.sendRealTimeEvent('visibleColumnsChange',
        {
          'scrollPercentage': scrollPercentage
        });
    }
  }

  /**
   * @param scrollLeft if true perform a scroll-action to the left, if false to the right
   */
  onScrollButtonClicked(scrollLeft: boolean): void {
    const container: HTMLElement = document.querySelector('#sp2-column-view');
    const numberOfIssues = this.issues.length;
    const scrollAmount = (container.scrollWidth / numberOfIssues);

    if (scrollLeft) {
      container.scrollLeft -= scrollAmount;
    }
    else {
      container.scrollLeft += scrollAmount;
    }
  }

  getBoardData(): Observable<BoardData> {
    throw new Error('Method not implemented.');
  }

}
